import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSignIn } from 'react-auth-kit';
import axios from 'axios';
import { useIntl } from 'react-intl';
import { apiAuthOtp, apiAuthSignup } from 'api/auth';
import { extractSignupSession, parseSignupCaptchaRequirement } from 'api/responseTypes';
import type { AuthSignupRequest } from 'api/requestTypes';
import {
  clearSignupCaptchaPersisted,
  writeSignupCaptchaPersisted,
} from 'utils/signupCaptchaStorage';
import { THIRTY_DAYS_IN_SECONDS, THREE_MINUTES_IN_SECONDS } from 'utils/constants';
import { useTimer } from 'hooks/useTimer';
import { usePopup } from 'context/PopupContext/PopupContext';
import type { Credential } from './usePhoneAuthStep';

export const OTP_LENGTH = 6;

interface UseOtpAuthStepOptions {
  credential: Credential;
  onSessionRefresh: (session: string) => void;
  onBackToPhoneStep: () => void;
  /** Called after successful sign-in so the parent can react (e.g. close a modal). */
  onSuccess?: () => void;
}

export const useOtpAuthStep = ({
  credential,
  onSessionRefresh,
  onBackToPhoneStep,
  onSuccess,
}: UseOtpAuthStepOptions) => {
  const signIn = useSignIn();
  const { showPopup } = usePopup();
  const { formatMessage } = useIntl();
  const { timer, resetTimer } = useTimer(THREE_MINUTES_IN_SECONDS);
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const [isResending, setIsResending] = useState(false);

  const {
    handleSubmit,
    control,
    setValue,
    setError,
    clearErrors,
    formState: { isSubmitting, errors },
    watch,
  } = useForm<{ otp: number[] }>({
    defaultValues: { otp: Array.from<number>({ length: OTP_LENGTH }).fill(0) },
  });

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleInputChange = (value: number, index: number) => {
    clearErrors('otp');
    setValue(`otp.${index}`, value);
    if (value != null && index < OTP_LENGTH - 1) {
      (inputRefs.current[index + 1] as HTMLInputElement | null)?.focus();
    }
  };

  const handleKeyDown = (index: number, event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Backspace' && watch('otp')[index] === 0 && index > 0) {
      (inputRefs.current[index - 1] as HTMLInputElement | null)?.focus();
    }
  };

  const onSubmit = async (data: { otp: number[] }) => {
    try {
      const response = await apiAuthOtp({
        otp: data.otp.join(''),
        phone: credential.phone,
        session: credential.session,
      });
      signIn({
        token: response.accessToken,
        expiresIn: response.expiresIn,
        tokenType: response.type,
        refreshToken: response.refreshToken,
        refreshTokenExpireIn: THIRTY_DAYS_IN_SECONDS,
        authState: { phone: credential.phone },
      });
      clearSignupCaptchaPersisted();
      onSuccess?.();
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.status === 400 && e.response.data.session) {
        onSessionRefresh(e.response.data.session);
        setError('otp', {
          type: 'manual',
          message:
            typeof e.response.data.message === 'string' ? e.response.data.message : undefined,
        });
      } else if (axios.isAxiosError(e) && e.response?.status === 401) {
        resetTimer(THREE_MINUTES_IN_SECONDS);
        setError('otp', { type: 'manual' });
      } else {
        showPopup({ message: e instanceof Error ? e.message : String(e), type: 'ERROR' });
      }
    }
  };

  const onResend = async () => {
    setIsResending(true);
    try {
      const payload: AuthSignupRequest = { phone: credential.phone };
      const response = await apiAuthSignup(payload);
      const session = extractSignupSession(response);
      if (!session) {
        showPopup({
          message: formatMessage({
            id: 'loginModal.signupUnexpectedResponse',
            defaultMessage: 'Something went wrong. Please try again.',
          }),
          type: 'ERROR',
        });
        return;
      }
      if (parseSignupCaptchaRequirement(response).required) writeSignupCaptchaPersisted();
      onSessionRefresh(session);
      resetTimer(THREE_MINUTES_IN_SECONDS);
      clearErrors('otp');
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.status === 403) {
        writeSignupCaptchaPersisted();
        showPopup({
          message: formatMessage({
            id: 'loginOtpModal.resendCaptchaBlocked',
            defaultMessage:
              'Verification required. Please re-enter your phone number and complete the captcha.',
          }),
          type: 'ERROR',
        });
        onBackToPhoneStep();
        return;
      }
      showPopup({ message: e instanceof Error ? e.message : String(e), type: 'ERROR' });
    } finally {
      setIsResending(false);
    }
  };

  return {
    control,
    inputRefs,
    errors,
    isSubmitting,
    isResending,
    timer,
    handleSubmit: handleSubmit(onSubmit),
    handleInputChange,
    handleKeyDown,
    onResend,
  };
};
